<template>
    <div
        :class="[
            getClass,
            { 'is--editable': hasEditMode },
            { 'container-hidden': isContainerVisuallyHidden }
        ]"
        class="grid-stack-item-content relative rounded-lg flex flex-col h-full w-full widget__wrap"
        data-testid="widget-container"
        :style="getStyles"
        @mouseenter="isHovered = true"
        @mouseleave="isHovered = false"
    >
        <div
            v-if="showWidgetHeader"
            class="flex items-center widget-header py-1"
            data-testid="widget-header"
        >
            <div
                class="flex overflow-x-auto overflow-y-hidden w-full flex-row items-center justify-between"
            >
                <i
                    v-if="widgetComponentTypeTemplateSettings.showWidgetTemplateIcon"
                    class="icon-lg widget-header-title mx-2"
                    :class="widget.WidgetTemplateIcon"
                />
                <base-widget-title
                    v-if="showWidgetTitle"
                    :title="widgetLocalData.Title"
                />
                <div
                    v-if="widgetComponentTypeTemplateSettings.showWidgetTimeFrame"
                    class="flex justify-start px-4 widget-header-times"
                >
                    <RedesignTimeFrame :widget="widget" />
                </div>
                <portal-target
                    :name="`widget-header__${widget.WidgetID}`"
                    class="hidden lg:flex w-full items-center justify-between"
                />
            </div>
            <div
                v-if="isDesktop"
                class="flex items-center flex-row gap-1"
            >
                <portal-target
                    :name="`widget-header__${widget.WidgetID}-table-action`"
                />
                <span
                    v-if="!!widgetHelpInfo"
                    class="flex items-center p-1 hover:bg-primary-100 rounded"
                    @click="openPreviewHelpDialog"
                >
                    <i class="vc-icon-info icon-lg text-primary cursor-help" />
                </span>
                <portal-target
                    :name="`widget-header__${widget.WidgetID}-action`"
                />
                <RedesignWidgetAction
                    v-if="widgetComponentTypeTemplateSettings.showWidgetActions"
                    data-testid="widget-actions"
                    :key="widget.WidgetID"
                    :editable="editable"
                    :edit-mode="editNotesMode"
                    :widget="widgetLocalData"
                    class="flex items-center widget-header-action widget-action rounded px-2 py-1-5"
                    @on-manage-notes="tryManageNotes"
                    @on-add-note="onAddNote"
                    @on-duplicate="duplicateWidget"
                    @on-remove="removeWidget"
                    @on-show-update-dialog="openUpdateDialog"
                >
                    <template #default>
                        <portal-target
                            v-if="haveFooterPortal"
                            :name="`widget-actions__${widget.WidgetID}`"
                            class="hidden lg:flex w-full items-center justify-between"
                        />
                    </template>
                </RedesignWidgetAction>
            </div>
        </div>
        <!-- Tongue toggle at bottom center (CSS controls visibility) -->
        <div
            v-if="isDesktop"
            class="widget-tongue"
            :class="{ 'is-active': isContainerHidden, 'is-visible': isHovered }"
            :title="isContainerHidden ? $t('widget.showContainer') : $t('widget.hideContainer')"
            @click="toggleContainerVisibility"
        >
            <RedesignBaseIcon
                :name="isContainerHidden ? 'Maximize2' : 'Minimize2'"
                :size="10"
            />
        </div>
        <!-- Floating edit button when container is hidden (always rendered, CSS controls visibility) -->
        <div
            v-if="isContainerHidden && isDesktop"
            class="floating-edit-btn"
            :class="{ 'is-visible': isHovered }"
        >
            <RedesignWidgetAction
                :key="`floating-${widget.WidgetID}`"
                :editable="editable"
                :edit-mode="editNotesMode"
                :widget="widgetLocalData"
                class="flex items-center justify-center"
                @on-manage-notes="tryManageNotes"
                @on-add-note="onAddNote"
                @on-duplicate="duplicateWidget"
                @on-remove="removeWidget"
                @on-show-update-dialog="openUpdateDialog"
            >
                <template #default>
                    <portal-target
                        v-if="haveFooterPortal"
                        :name="`widget-actions__${widget.WidgetID}`"
                        class="flex items-center justify-between"
                    />
                </template>
            </RedesignWidgetAction>
        </div>
        <div
            v-if="inView"
            :id="`widgetId-${widget.WidgetID}`"
            class="flex w-full flex-col widget-container"
        >
            <component
                :is="getComponentTypeAndSetData(widget)"
                v-if="showWidgetContent"
                ref="widgetRef"
                :data="widgetLocalData"
                :editable="editable"
                :on-edit-mode="editNotesMode"
                class="widget"
                v-bind="widgetLocalData?.WidgetLayout"
                @on-update="onUpdate"
                @remove-item="removeWidget"
                @duplicate-widget="duplicateWidget"
                @on-show-info="onShowInfo"
                @on-show-update-dialog="openUpdateDialog"
            />
            <portal-target
                v-if="haveFooterPortal"
                :name="`widget-footer__${widget.WidgetID}`"
            />
        </div>
        <template-preview-info-dialog
            ref="templatePreviewInfoRef"
        />
    </div>
</template>

<script setup lang="ts">
import get from 'lodash/get'
import set from 'lodash/set'
import { INFO_TYPE_ID, TABLE_TYPE_ID } from '@/enum/widgetDataTypes'
import widgetComponentTypes from '@/enum/widgetComponentTypes'
import { getWidgetDataType, getWidgetEndpoint, getWidgetRefreshInterval, isRealtimeWidget } from '@/helpers/widgetUtils'
import RedesignWidgetAction from '@/components/RedesignComponents/Widgets/RedesignWidgetAction.vue'
import RedesignBaseIcon from '@/components/RedesignBaseComponents/RedesignBaseIcon.vue'
import TemplatePreviewInfoDialog from '@/components/Widgets/AddWidgetsForm/TemplatePreviewInfoDialog.vue'
import { computed, ref, watch } from 'vue'
import { ObjectAnyType, WidgetList } from '@/types/dashboard'
import BaseWidgetTitle from '@/components/BaseWidgetTitle.vue'
import { WidgetComponentsEnum } from '@/components/RedesignComponents/Widgets/widgets.enum'
import { editableWidgetId, openWidgetEditModal, widgetModelData } from '@/composables/useWidgetEditModal'
import { isDesktop } from '@/composables/useBreakpoints'
import RedesignTimeFrame from '@/components/RedesignComponents/RedesignTableComponents/RedesignTimeFrame.vue'
import { getPreviewLayout, widgetPreviewHeaderTextFontSize } from '@/composables/usePreviewLayout'
import { activeLayoutFontSize, getActiveLayout, getLayoutColors } from '@/composables/globalData/useLayouts'
import {
    DEFAULT_FONT_SIZE_PX,
    widgetComponentTypeSettingDefault,
    widgetComponentTypeSettings
} from '@/enum/default-layout-config'
import { getHelpByWidgetsTemplateID } from '@/composables/globalData/useTemplatesDictionaryData'

// TODO: UpdateDialog ConfigDialog need to change
const COMPONENTS = {
    ...WidgetComponentsEnum
}

const TABLE_DATA_TYPE_ID = '4'

/* Props */
const props = withDefaults(
    defineProps<{
        showWidgetContent?: boolean
        editable?: boolean
        gridEditMode?: boolean
        previewMode?: boolean
        widget: WidgetList
        layoutType?: string
        inViewById?: ObjectAnyType
    }>(),
    {
        layoutType: '',
        gridEditMode: false,
        inViewById: () => ({})
    }
)
/* Emits */
const emit = defineEmits([
    'remove-item',
    'duplicate-widget',
    'update-item',
    'update:widgetTime'
])

/*Data*/
const editNotesMode = ref(false)
const showPreviewInfoDialog = ref(false)
const widgetRef = ref()
const templatePreviewInfoRef = ref<typeof TemplatePreviewInfoDialog>()
const isHovered = ref(false)

/* Computed */
const widgetLocalData = computed(() => {
    return hasEditMode.value && widgetModelData.value ? widgetModelData.value : props.widget
})
const hasEditMode = computed(() => {
    return editableWidgetId.value === props.widget.WidgetID
})
const haveFooterPortal = computed(() => {
    return get(props.widget, 'DataTypeID', -1) === TABLE_TYPE_ID || INFO_TYPE_ID
})
const showWidgetTitle = computed(() => {
    return get(widgetLocalData.value.WidgetLayout, 'displayWidgetTitle', true) && widgetComponentTypeTemplateSettings.value?.showWidgetTitle
})
const isContainerHidden = computed(() => {
    return get(widgetLocalData.value.WidgetLayout, 'hideContainer', false)
})
// Visually hide container only when hideContainer is true AND not in grid edit mode
const isContainerVisuallyHidden = computed(() => {
    return isContainerHidden.value && !props.gridEditMode
})
const inView = computed(() => {
    return props.inViewById[props.widget.WidgetID] || true
})
const getPadding = computed(() => {
    const padding = get(widgetLocalData.value.WidgetLayout, 'padding', 2)
    return { padding: `${padding}px` }
})
const getStyles = computed(() => {
    const layoutTitleStyles = props.previewMode ? widgetPreviewHeaderTextFontSize.value : activeLayoutFontSize.value
    const layoutTitleFontSize = get(layoutTitleStyles, 'fontSize', DEFAULT_FONT_SIZE_PX)

    let styles: {
        color?: string,
        background?: string,
        '--layout-title-font-size': string,
        '--colorTableContentText-color'?: string
        '--warning-color'?: string,
        '--limit-color'?: string
        '--colorWidgetHeaderText-color'?: string
        '--colorWidgetTimeText-color'?: string
    } = {
        color: '',
        background: '',
        '--layout-title-font-size': layoutTitleFontSize
    }
    const _defaultColors = getWidgetDefaultColors()
    const widgetLayoutColors = widgetLocalData.value?.WidgetLayout?.colors || {}
    const colors = {
        ..._defaultColors,
        ...widgetLayoutColors
    }

    let rootBGColor: string | undefined = getComputedStyle(document.documentElement).getPropertyValue('--colorWidgetBackground-color')
    if (document.getElementById('dashboard-wrapper-preview') || props.previewMode) {
        //TODO: getElementById('dashboard-wrapper-preview') returns null
        rootBGColor = document.getElementById('dashboard-wrapper-preview')?.style.getPropertyValue('--colorWidgetBackground-color') || _defaultColors.background
    }

    styles.color = colors.fonts
    styles.background = colors.background === _defaultColors.background ? rootBGColor : colors.background
    styles['--colorWidgetTimeText-color'] = colors.fonts
    styles['--colorWidgetHeaderText-color'] = colors.widgetHeader

    const padding = getPadding.value

    const { warningColor = undefined, limitColor = undefined } = widgetLocalData.value.WidgetLayout

    styles['--warning-color'] = warningColor
    styles['--limit-color'] = limitColor

    const isBorder = widgetLocalData.value?.WidgetLayout?.displayBorder === undefined ? true : widgetLocalData.value?.WidgetLayout?.displayBorder
    // Show border when: border is enabled AND container is not visually hidden (considers global edit mode)
    const border = (isBorder && !isContainerVisuallyHidden.value) ? { border: '1px solid ' + colors.frames } : {}

    // Don't apply padding when container is hidden to save space
    styles = {
        ...styles,
        ...border,
        ...(isContainerVisuallyHidden.value ? {} : padding),
    }

    return styles
})
const getClass = computed(() => {
    const DataTypeID = get(props.widget, 'DataTypeID', '0')
    const baseClass = widgetHeaderClass.value
    if (DataTypeID?.toString() === TABLE_DATA_TYPE_ID) {
        return `has-margin ${baseClass}`
    }
    return baseClass
})
const widgetComponentTypeTemplateSettings = computed(() => {
    const dataType = getWidgetDataType(props.widget)
    return widgetComponentTypeSettings[dataType] ?? widgetComponentTypeSettingDefault
})
const showWidgetHeader = computed(() => {
    // Hide header when container is hidden to save space
    if (isContainerHidden.value) {
        return false
    }
    return widgetComponentTypeTemplateSettings.value.showWidgetHeader
})
const widgetHeaderClass = computed(() => {
    return `header--${widgetComponentTypeTemplateSettings.value.widgetHeaderType}`
})
const widgetHelpInfo = computed(() => {
    const dataType = getWidgetDataType(props.widget)
    return widgetComponentTypeSettings[dataType]?.showWidgetInfo && getHelpByWidgetsTemplateID(props.widget.TemplateID)
})

/* Methods */
function getWidgetDefaultColors () {
    const previewLayout = props.previewMode ? getPreviewLayout() : undefined
    const layout = previewLayout || getActiveLayout()
    const { colorWidgetBorder, colorWidgetBackground, mainTextColor, colorWidgetHeaderText } = getLayoutColors(layout)

    return {
        background: colorWidgetBackground,
        frames: colorWidgetBorder,
        fonts: mainTextColor,
        widgetHeader: colorWidgetHeaderText
    }
}

function tryManageNotes () {
    editNotesMode.value = !editNotesMode.value
}

function removeWidget () {
    emit('remove-item', props.widget)
}

function duplicateWidget () {
    emit('duplicate-widget', props.widget)
}

function onUpdate (widget: WidgetList, silence: boolean) {
    editNotesMode.value = false
    emit('update-item', widget, silence)
}

function getComponentTypeAndSetData (widget: WidgetList) {
    const dataTypeId = getWidgetDataType(widget)
    const refreshInterval = getWidgetRefreshInterval(widget)

    let componentType = widgetComponentTypes[dataTypeId]
    const endPoint = setComponentEndPoint(widget)

    set(widget, 'ComponentType', componentType)
    set(widget, 'DataTypeID', dataTypeId)
    set(widget, 'EndPoint', endPoint)
    set(widget, 'DefaultRefreshInterval', refreshInterval)

    if (componentType === 'SocketsRealTimeTable') {
        componentType = 'RedesignTableData'
    }

    return COMPONENTS[componentType as keyof typeof COMPONENTS]
}

function setComponentEndPoint (widget: WidgetList) {
    return getWidgetEndpoint(widget)
}

function onAddNote () {
    try {
        widgetRef.value?.onAddNote()
    } catch (e) {
        console.log(e)
    }
}

function toggleContainerVisibility () {
    const updatedWidget = {
        ...props.widget,
        WidgetLayout: {
            ...props.widget.WidgetLayout,
            hideContainer: !isContainerHidden.value
        }
    }
    emit('update-item', updatedWidget, false)
}

function onShowInfo () {
    showPreviewInfoDialog.value = true
}

function onUpdateDialog (widget: WidgetList) {
    //const data = filteredDefaultDataParams(widget)
    emit('update-item', widget)
}

function openUpdateDialog (widget?: WidgetList) {
    openWidgetEditModal({
        widgetData: widget ?? props.widget,
        onSave: onUpdateDialog
    })
}
function openPreviewHelpDialog () {
    templatePreviewInfoRef.value?.open(widgetHelpInfo.value)
}

/* Watch */
watch(
    () => props.editable,
    (isEditable) => {
        if (!isEditable) {
            editNotesMode.value = false
        }
    }
)
</script>
<style lang="scss" scoped>
$headerHeightAddition: 8px;

.widget__wrap {
  --headerHeight: max(36px, calc(var(--layout-title-font-size) + #{$headerHeightAddition}));
}

.widget-header {
  height: var(--headerHeight);
}

.widget-container {
  height: calc(100% - var(--headerHeight));
}

.widget {
    @apply h-full;
}

.widget__wrap {
    &.header--absolute {
        .widget-header {
            @apply absolute top-0 inset-x-0 h-auto;
        }

        .widget-container {
            @apply h-full;
        }
    }

    &.header--sticky {
        .widget-header {
            @apply sticky top-0 w-full;
        }
    }

    &.container-hidden {
        background: transparent !important;
        border: none !important;
        padding: 0 !important;

        .widget-container {
            height: 100%;
        }

        /* Remove padding from inner content when container is hidden */
        .widget-container > .widget {
            padding: 0 !important;

            > div {
                padding-top: 0 !important;
                padding-bottom: 0 !important;
            }
        }
    }
}

/* Tongue toggle button at bottom center */
.widget-tongue {
    @apply absolute z-20 flex items-center justify-center cursor-pointer;
    bottom: 4px;
    left: 50%;
    transform: translateX(-50%);
    width: 36px;
    height: 18px;
    background: var(--colorWidgetBackground-color);
    border: 1px solid var(--colorWidgetBorder-color);
    border-radius: 4px;
    color: var(--colorWidgetHeaderText-color);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;

    &.is-visible {
        opacity: 1;
        pointer-events: auto;
    }

    &:hover {
        opacity: 0.8;
    }

    &.is-active {
        background: var(--primary-color);
        color: white;
        border-color: var(--primary-color);

        &:hover {
            opacity: 0.9;
        }
    }
}

/* Edit button (3-dots) - only shown when container hidden */
.floating-edit-btn {
    @apply absolute z-10;
    top: 12px;
    right: 12px;
    background: var(--colorWidgetBackground-color);
    border-radius: 4px;
    padding: 2px 4px;
    border: 1px solid var(--colorWidgetBorder-color);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;

    &.is-visible {
        opacity: 1;
        pointer-events: auto;
    }
}

/* RTL support for floating edit button */
[dir='rtl'] .floating-edit-btn {
    right: auto;
    left: 12px;
}

</style>
<style lang="scss">
.grid-stack > .grid-stack-item > .grid-stack-item-content {
    overflow: inherit;

    .widget {
        overflow-x: hidden
    }

    &.is--editable {
        @apply shadow-active-widget;
    }
}

.grid-stack-item:has(.grid-stack-item-content.is--editable) {
    @apply overflow-visible;
}

.widget-header-title, .widget-header-action {
    color: var(--colorWidgetHeaderText-color);
}

.widget-header-times {
    color: var(--colorWidgetTimeText-color);
}

.widget__wrap:has(.is--status-warning) {
    --pulse-animate-color: var(--warning-color);
    box-shadow: 0 0 8px 0 var(--pulse-animate-color);
}

.widget__wrap:has(.is--status-warning:not(.no-animation)) {
    animation: blink-shadow-animation 2s infinite;

    .widget-counter-card {
        animation: pulse-text-animation 2s infinite;
    }
}

.widget__wrap:has(.is--status-limit) {
    --pulse-animate-color: var(--limit-color);
    box-shadow: 0 0 8px 0 var(--pulse-animate-color);
}

.widget__wrap:has(.is--status-limit:not(.no-animation)) {
    animation: blink-shadow-animation 2s infinite;

    .widget-counter-card {
        animation: pulse-text-animation 2s infinite;
    }
}


@keyframes blink-shadow-animation {
    0% {
        box-shadow: 0 0 8px 0 var(--pulse-animate-color);
    }
    50% {
        box-shadow: 0 0 12px 0 transparent;
        border-color: var(--pulse-animate-color);
    }
    100% {
        box-shadow: 0 0 8px 0 var(--pulse-animate-color);
    }
}

@keyframes pulse-text-animation {
    0% {
        color: inherit;
    }
    50% {
        color: var(--pulse-animate-color);
    }
    100% {
        color: inherit;
    }
}

</style>
